import { UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from 'api/apiClient/apiClient'
import { OPERATIONAL_EVENT_CONFIG_QUERY_KEY, OPERATIONAL_EVENT_QUERY_KEY } from 'api/events/events.constants'
import { refetchOperationalEventsAfterInFlight } from 'api/events/events.invalidation.helper'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { usePusherSocketIdHeaders } from 'ui/components/PusherContext/PusherContext'

import { EventChangeRequest, EventConfig, EventPost, EventStatus } from 'views/EventsView/components/events.helper'

export type getAllEventMutationParams = {
  ids?: number[]
  object_category?: string
  object_name?: string
  attribute?: string
}

export function useGetAllEventMutation(): UseMutationResult<EventPost[], unknown, getAllEventMutationParams, unknown> {
  const { activeSystem } = useAuth()

  return useMutation({
    mutationFn: (params: getAllEventMutationParams): Promise<EventPost[]> => {
      if (!activeSystem) {
        return Promise.resolve([])
      }

      return apiClient<EventPost[]>(`operational_events`, {
        method: 'GET',
        params: { system_id: activeSystem.id, ...params },
        addBracketsToArrayParamKeys: false,
      })
    },
  })
}

export function useGetAllEvents(
  system_id?: number,
  start_time?: ISODateTime,
  end_time?: ISODateTime,
  status?: EventStatus,
  ids?: number[]
) {
  return useQuery({
    queryKey: [OPERATIONAL_EVENT_QUERY_KEY, { system_id, start_time, end_time, status, ids }],

    queryFn: async () => {
      const data = apiClient<EventPost[]>(`operational_events`, {
        method: 'GET',
        params: {
          ids,
          system_id,
          start_time,
          end_time,
          status,
        },
      })

      if (!data) {
        return []
      }
      return data
    },

    enabled: system_id !== null && system_id !== undefined,
  })
}

export function useGetEvent(id?: number | null) {
  return useQuery({
    queryKey: [OPERATIONAL_EVENT_QUERY_KEY, id],

    queryFn: async () => {
      const data = apiClient<EventPost>(`operational_events/${id}`, {
        method: 'GET',
      })

      if (!data) {
        return null
      }

      return data
    },

    enabled: id !== null && id !== undefined,
  })
}

export function useEventCreateMutation() {
  const queryClient = useQueryClient()
  const clientSocketIdForHeader = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: (data: EventPost) =>
      apiClient<EventPost>(`operational_events/create`, { data, method: `POST`, headers: clientSocketIdForHeader }),

    onSuccess: () => {
      refetchOperationalEventsAfterInFlight(queryClient)
    },
  })
}

export function useEventUpdateMutation() {
  const queryClient = useQueryClient()
  const clientSocketIdForHeader = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: (data: EventPost) =>
      apiClient<EventPost>(`operational_events/${data.id}/update`, {
        data,
        method: `PATCH`,
        headers: clientSocketIdForHeader,
      }),

    onSuccess: () => {
      refetchOperationalEventsAfterInFlight(queryClient)
    },
  })
}

export function useEventDeleteMutation() {
  const queryClient = useQueryClient()
  const clientSocketIdForHeader = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: (id: number) =>
      apiClient<void>(`operational_events/${id}/delete`, { method: `DELETE`, headers: clientSocketIdForHeader }),

    onSuccess: () => {
      refetchOperationalEventsAfterInFlight(queryClient)
    },
  })
}

export function useGetEventConfig(system_id?: number) {
  return useQuery({
    queryKey: [OPERATIONAL_EVENT_CONFIG_QUERY_KEY],

    queryFn: async () => {
      const data = apiClient<EventConfig>(`operational_event_config`, {
        method: 'GET',
        params: {
          system_id,
        },
      })

      if (!data) {
        return {}
      }

      return data
    },

    enabled: system_id !== null && system_id !== undefined,
  })
}

export function useCreateEventChangeRequestMutation() {
  const queryClient = useQueryClient()
  const clientSocketIdForHeader = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: ({ data, event_id }: { data: EventChangeRequest; event_id: number }) =>
      apiClient<EventChangeRequest>(`operational_events/${event_id}/change_request/create`, {
        data,
        method: `POST`,
        headers: clientSocketIdForHeader,
      }),

    onSuccess: () => {
      refetchOperationalEventsAfterInFlight(queryClient)
    },
  })
}

export function useUpdateEventChangeRequestMutation() {
  const queryClient = useQueryClient()
  const clientSocketIdForHeader = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: ({
      data,
      event_id,
      change_request_id,
    }: {
      data: EventChangeRequest
      event_id: number
      change_request_id: number
    }) =>
      apiClient<EventChangeRequest>(`operational_events/${event_id}/change_request/${change_request_id}/update`, {
        data,
        method: `POST`,
        headers: clientSocketIdForHeader,
      }),

    onSuccess: () => {
      refetchOperationalEventsAfterInFlight(queryClient)
    },
  })
}

export function useApproveEventChangeRequestMutation() {
  const queryClient = useQueryClient()
  const clientSocketIdForHeader = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: ({ event_id, change_request_id }: { event_id: number; change_request_id: number }) =>
      apiClient<void>(`operational_events/${event_id}/change_request/${change_request_id}/approve`, {
        method: `POST`,
        headers: clientSocketIdForHeader,
      }),
    onSuccess: () => {
      refetchOperationalEventsAfterInFlight(queryClient)
    },
  })
}

export function useRejectEventChangeRequestMutation() {
  const queryClient = useQueryClient()
  const clientSocketIdForHeader = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: ({ event_id, change_request_id }: { event_id: number; change_request_id: number }) =>
      apiClient<void>(`operational_events/${event_id}/change_request/${change_request_id}/reject`, {
        method: `POST`,
        headers: clientSocketIdForHeader,
      }),

    onSuccess: () => {
      refetchOperationalEventsAfterInFlight(queryClient)
    },
  })
}

export function useDeleteEventChangeRequestMutation() {
  const queryClient = useQueryClient()
  const clientSocketIdForHeader = usePusherSocketIdHeaders()

  return useMutation({
    mutationFn: ({ event_id, change_request_id }: { event_id: number; change_request_id: number }) =>
      apiClient<void>(`operational_events/${event_id}/change_request/${change_request_id}/delete`, {
        method: `DELETE`,
        headers: clientSocketIdForHeader,
      }),

    onSuccess: () => {
      refetchOperationalEventsAfterInFlight(queryClient)
    },
  })
}

export function useGetAllEventChangeRequests(
  system_id: number | undefined,
  request_status: 'Pending' | 'Approved' | 'Rejected',
  enabled?: boolean,
  start_time?: ISODateTime,
  end_time?: ISODateTime,
  ids?: number[]
) {
  return useQuery({
    queryKey: [OPERATIONAL_EVENT_QUERY_KEY, { system_id, start_time, end_time, request_status, ids }],

    queryFn: async () => {
      const data = apiClient<EventPost[]>(`operational_events/change_request`, {
        method: 'GET',
        params: {
          system_id,
          request_status,
          ids,
          start_time,
          end_time,
        },
      })

      if (!data) {
        return []
      }
      return data
    },

    enabled: enabled !== false && system_id !== null && system_id !== undefined,
  })
}
